import React, { useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { useAppThunkDispatch, RootState } from "redux/store";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { styles } from "../../style";
import { Dialog } from "component/shared/dialog";
import { CustomAutocomplete } from "component/shared/auto-complete";
import { getDripCampaignSubscriptionsForLeads, getActiveDripCampaigns, getLeadsOptimized } from "redux/selector";
import { createCampaignSubscription } from "redux/slice/campaign-subscriptions";
import * as yup from "yup";
import { validate } from "shared/yup";
import { CampaignSubscriptionStatus } from "type/campaign-subscription-status";
import * as env from "shared/env";

interface Props extends WithStyles<typeof styles> {
	onClose: Function;
	leadIds: number[];
}

const Component: React.FC<Props> = ({ onClose, leadIds, classes = {} }) => {
	const dispatch = useAppThunkDispatch();

	// Redux selectors
	const campaigns = useSelector(getActiveDripCampaigns);
	const subscriptions = useSelector((state: RootState) => getDripCampaignSubscriptionsForLeads(state, leadIds));
	const leads = useSelector(getLeadsOptimized);

	// Local state
	const [campaignId, setCampaignId] = useState<number | undefined>(undefined);
	const [submitted, setSubmitted] = useState(false);
	const [input, setInput] = useState("");

	// Validation schema
	const validationSchema = {
		campaignId: yup
			.string()
			.required("Please select a campaign."),
	};

	const addCampaign = useCallback(() => {
		const state = { campaignId };
		const errors = validate(validationSchema, state);

		if (!errors && campaignId) {
			leadIds.forEach((id) => {
				const existingSubscription = subscriptions.find((subscription) => {
					return subscription.campaign.id === campaignId && subscription.lead.id === id
				});
				const campaign = campaigns.find(item => item.id === campaignId);
				const lead = leads.find((item) => item.id === id);
				if (!existingSubscription && campaign && lead) {
					dispatch(createCampaignSubscription({ subscription: {
						lead,
						campaign,
						status: CampaignSubscriptionStatus.ACTIVE,
					}}));
				}
			});
			handleClose();
		} else {
			setSubmitted(true);
		}
	}, [campaignId, leadIds, subscriptions, campaigns, leads, dispatch, validationSchema]);

	const handleClose = useCallback(() => {
		onClose();
		setCampaignId(undefined);
		setSubmitted(false);
		setInput("");
	}, [onClose]);

	const handleCampaignChange = useCallback((value: number) => {
		setCampaignId(value);
	}, []);

	const state = { campaignId };
	const errors = validate(validationSchema, state);
	const campaignOptions = campaigns.map((campaign) => ({
		value: campaign.id,
		label: campaign.name,
	}));

	return (
		<Dialog
			open={true}
			onClose={handleClose}
			fullWidth={true}
			maxWidth="xs" 
		>
			<Mui.DialogTitle>Add To Campaign</Mui.DialogTitle>
			<Mui.DialogContent dividers className={classes.dialogContent}>
				<Mui.Grid container alignItems="center" spacing={1}>
					<Mui.Grid item xs={12}>
						<Mui.DialogContentText className={classes.dialogText}>
							Subscribe all selected contacts to a Marketing Automation Campaign.
						</Mui.DialogContentText>
					</Mui.Grid>
					<Mui.Grid item xs={12}>
						<CustomAutocomplete
							options={campaignOptions}
							textInputHeight={true}
							label="Select Campaign"
							optionLabelExtractor={(option) => option.label}
							fullWidth
							margin="dense"
							placeholder="Select a campaign"
							onInputChange={(event, value, reason) => {
								if (reason === "reset") {
									setInput("");
								} else {
									setInput(value);
								}
							}}
							onChange={(event, option) => {
								if (option && option.value) {
									setCampaignId(option.value as number);
									setInput(option.label);
								}
							}}
							inputValue={input}
							helperText={submitted && errors && errors.campaignId ? errors.campaignId : undefined}
						/>
					</Mui.Grid>
				</Mui.Grid>
			</Mui.DialogContent>
			<Mui.DialogActions className={classes.dialogActions}>
				<Mui.Button 
					onClick={handleClose}
					variant="outlined"
					color={"primary"}
				>
					Cancel
				</Mui.Button>
				<Mui.Button
					onClick={addCampaign}
					variant="contained"
					color={"primary"}
					disabled={submitted && !campaignId}
				>
					Save
				</Mui.Button>
			</Mui.DialogActions>
		</Dialog>
	);
};

export const BulkCampaignDialog = withStyles(Component, styles);